import { useUser } from "@clerk/expo";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  CreatePanelRequest,
  GetPanelsRequest,
  UpdatePanelRequest,
} from "@/types/requests/panels";
import { Folder, Panel } from "@/types/resources";
import {
  CreatePanelResponse,
  GetPanelsResponse,
  UpdatePanelResponse,
} from "@/types/responses";

const useCreatePanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/panels");

  const createPanel = useCallback(
    async (data: CreatePanelRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          CreatePanelResponse,
          AxiosResponse<CreatePanelResponse>,
          CreatePanelRequest
        >("", {
          demographics: data.demographics,
          description: data.description,
          folder_id: data.folder_id,
          is_default: data.is_default,
          name: data.name.trim(),
        });
        return response.data.panel;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    createPanel,
    error,
    loading,
  };
};

const useUpdatePanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/panels");

  const updatePanel = useCallback(
    async (panelId: string, data: UpdatePanelRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.put<
          UpdatePanelResponse,
          AxiosResponse<UpdatePanelResponse>,
          UpdatePanelRequest
        >(`/${panelId}`, {
          demographics: data.demographics,
          description: data.description,
          folder_id: data.folder_id,
          is_default: data.is_default,
          name: data.name?.trim(),
        });
        return response.data.panel;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    loading,
    updatePanel,
  };
};

const useArchivePanel = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/panels");

  const archivePanel = useCallback(
    async (panelId: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        await client.delete(`/${panelId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    archivePanel,
    error,
    loading,
  };
};

const useGetPanels = ({
  archived,
  folder_id,
  items_per_page = 20,
  name,
  page = 1,
}: GetPanelsRequest = {}) => {
  const { user } = useUser();
  const client = useAxiosClient("/panels");

  const [count, setCount] = useState<number>(0);
  const [currentFolder, setCurrentFolder] = useState<
    (Folder & { ancestors: Folder[] }) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  // Mirror the original mount behavior: the fetch effect set loading true only
  // when a user was present, so seed loading from that condition here.
  const [loading, setLoading] = useState(() => !!user);
  const [panels, setPanels] = useState<Panel[]>([]);

  const getPanels = useCallback(
    (params: GetPanelsRequest) => {
      client
        .get<GetPanelsResponse>("", { params })
        .then((response) => {
          setCount(response.data.count);
          setCurrentFolder(response.data.current_folder);
          setFolders(response.data.folders);
          setPanels((prev) =>
            (params.page ?? 1) > 1
              ? [...prev, ...response.data.panels]
              : response.data.panels
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client]
  );

  // Reset the list during render (not in an effect) when the filters change.
  // The initial mount needs no reset since the state already starts empty.
  const filterKey = JSON.stringify([folder_id, name]);
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setPanels([]);
    setFolders([]);
    setCurrentFolder(null);
  }

  // Re-enter the loading state during render when an input changes, mirroring
  // the fetch effect, which only ran (and set loading) when a user was present.
  const queryKey = JSON.stringify([
    user?.id ?? null,
    page,
    items_per_page,
    folder_id,
    name,
    archived,
  ]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    if (user) {
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    if (!user) return;
    getPanels({
      archived: archived || undefined,
      folder_id: name ? undefined : folder_id,
      items_per_page,
      name: name || undefined,
      page,
    });
  }, [user, page, items_per_page, folder_id, name, archived, getPanels]);

  const refresh = useCallback(() => {
    setPanels([]);
    setFolders([]);
    setCurrentFolder(null);
    setLoading(true);
    setError(null);
    getPanels({
      archived: archived || undefined,
      folder_id: name ? undefined : folder_id,
      items_per_page,
      name: name || undefined,
      page: 1,
    });
  }, [getPanels, items_per_page, folder_id, name, archived]);

  return {
    count,
    currentFolder,
    error,
    folders,
    loading,
    panels,
    refresh,
    setCount,
    setFolders,
    setPanels,
  };
};

export { useArchivePanel, useCreatePanel, useGetPanels, useUpdatePanel };
